import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Star } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';

// Setup → Tax Rates. Per-facility named tax rates; one can be default.
const blank = { name: '', rate: '', is_default: false };

const TaxRates = () => {
  const { facilityId } = useFacility();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const r = await apiClient.get(`/api/facilities/${facilityId}/tax-rates/`);
      setRows(r.data);
    } catch {
      setError('Could not load tax rates');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      name: form.name.trim(),
      rate: parseFloat(form.rate),
      is_default: !!form.is_default,
    };
    if (!payload.name || Number.isNaN(payload.rate)) {
      setError('Name and a numeric rate are required');
      return;
    }
    try {
      if (editing === 'new') {
        await apiClient.post(`/api/facilities/${facilityId}/tax-rates/`, payload);
      } else {
        await apiClient.put(
          `/api/facilities/${facilityId}/tax-rates/${editing}`, payload);
      }
      setEditing(null);
      await load();
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete tax rate "${r.name}"?`)) return;
    try {
      await apiClient.delete(`/api/facilities/${facilityId}/tax-rates/${r._id}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  const input =
    'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500';

  return (
    <div className="max-w-3xl">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-600">
            Setup
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Tax Rates
          </h1>
          <p className="text-[13px] text-slate-500 mt-0.5">
            Define the tax rates this facility charges. The default is
            applied to rent unless a fee specifies its own rate.
          </p>
        </div>
        <button
          onClick={() => { setForm(blank); setEditing('new'); setError(''); }}
          className="inline-flex items-center gap-1.5 py-2 px-3.5 rounded-lg bg-indigo-600 text-white text-[13px] font-semibold shadow-sm hover:bg-indigo-700 transition-colors"
        >
          <Plus className="h-4 w-4" /> New rate
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200/80 px-3 py-2.5 text-[13px] text-rose-700">
          {error}
        </div>
      )}

      {editing && (
        <form onSubmit={save}
          className="mb-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Name</label>
            <input className={input} value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="State sales tax" />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">Rate (%)</label>
            <input className={input} type="number" step="0.001" value={form.rate}
              onChange={(e) => setForm({ ...form, rate: e.target.value })}
              placeholder="6.25" />
          </div>
          <label className="flex items-center gap-2 text-[13px] text-slate-700 pb-2">
            <input type="checkbox" checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
            Default rate
          </label>
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit"
              className="py-2 px-4 rounded-lg bg-indigo-600 text-white text-[13px] font-semibold hover:bg-indigo-700">
              Save
            </button>
            <button type="button" onClick={() => setEditing(null)}
              className="py-2 px-4 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-slate-50/60 text-left">
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Name</th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Rate</th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Default</th>
                <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-400">No tax rates yet</td></tr>
              ) : rows.map((r) => (
                <tr key={r._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3.5 font-medium text-slate-900">{r.name}</td>
                  <td className="px-5 py-3.5 text-slate-600 tabular-nums">{r.rate}%</td>
                  <td className="px-5 py-3.5">
                    {r.is_default && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-600/20">
                        <Star className="h-3 w-3" /> Default
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => {
                        setForm({ name: r.name, rate: r.rate, is_default: r.is_default });
                        setEditing(r._id); setError('');
                      }} className="p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => remove(r)}
                        className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TaxRates;
